import {
  AmDateRange,
  AmDurationType,
  AmHolidayType,
  AmLocation,
  AmPaxAmount,
  AmRooms,
  daysBetween,
  Locations,
  TravelType,
} from '../common';
import { Service } from '../services';
import { changed, isBlank, isNotBlank, isNotNull, isNotPresent, isPresent } from '../utils';
import { AmSelectionState, BookingState, SelectionState, UxState } from './state';

export const isLoading = (state: BookingState, service: Service): boolean => !!state?.ux?.loading?.[service];

export const isNotLoading = (state: BookingState, service: Service): boolean => !isLoading(state, service);

export const locationsChanged = (
  { selection: { locations: prev } }: BookingState,
  { selection: { locations: next, activeLocationIndex } }: BookingState
): boolean =>
  changed(prev[activeLocationIndex], next[activeLocationIndex], 'origin', 'locationCode') ||
  changed(prev[activeLocationIndex], next[activeLocationIndex], 'destination', 'locationCode');

export const originChanged = (
  { selection: { locations: prev } }: BookingState,
  { selection: { locations: next, activeLocationIndex } }: BookingState
): boolean => changed(prev[activeLocationIndex], next[activeLocationIndex], 'origin', 'locationCode');

export const destinationChanged = (
  { selection: { locations: prev } }: BookingState,
  { selection: { locations: next, activeLocationIndex } }: BookingState
): boolean => changed(prev[activeLocationIndex], next[activeLocationIndex], 'destination', 'locationCode');

export const originCode = (locations: Locations): string => locations?.origin?.locationCode;
export const destinationCode = (locations: Locations): string => locations?.destination?.locationCode;

export const locationsAreSame = (locations: Locations): boolean => {
  const origin: string = originCode(locations);
  return isPresent(origin) && origin === destinationCode(locations);
};

export const locationsSet = ({ locations, activeLocationIndex }: SelectionState): boolean =>
  isPresent(locations[activeLocationIndex].origin) && isPresent(locations[activeLocationIndex].destination);

export const preDatesExcludingPaxAmountChanged = (
  { selection: prev }: BookingState,
  { selection: post }: BookingState
): boolean => {
  if (prev === post) {
    return false;
  }
  return (
    changed(
      prev.locations[prev.activeLocationIndex],
      post.locations[post.activeLocationIndex],
      'origin',
      'locationCode'
    ) ||
    changed(
      prev.locations[prev.activeLocationIndex],
      post.locations[post.activeLocationIndex],
      'destination',
      'locationCode'
    ) ||
    changed(prev, post, 'travelClass') ||
    changed(prev, post, 'travelType') ||
    changed(prev, post, 'directFlights')
  );
};

export const preDatesChanged = (prev: BookingState, post: BookingState): boolean =>
  preDatesExcludingPaxAmountChanged(prev, post) || changed(prev, post, 'selection', 'paxAmount');

export const preDatesOrDepartureChanged = (prev: BookingState, next: BookingState): boolean =>
  preDatesExcludingPaxAmountChanged(prev, next) || changed(prev, next, 'selection', 'travelDates', 'departureDate');

export const preDatesSelected = ({
  locations,
  paxAmount,
  travelClass,
  travelType,
  activeLocationIndex,
}: SelectionState): boolean => {
  if (
    isNotPresent(locations) ||
    isNotPresent(locations[activeLocationIndex].origin) ||
    isNotPresent(locations[activeLocationIndex].destination)
  ) {
    return false;
  }
  if (isNotPresent(paxAmount) || isNotPresent(paxAmount.adults)) {
    return false;
  }
  return isPresent(travelClass) && isPresent(travelType);
};

export const preDatesSelectedAndNotOneWay = (selection: SelectionState): boolean =>
  preDatesSelected(selection) && selection.travelType !== TravelType.ONE_WAY;

export const preDatesAndDepartureSelected = (selectState: SelectionState): boolean =>
  preDatesSelected(selectState) && !!selectState?.travelDates?.departureDate;

export const calendarChanged = (prev: BookingState, next: BookingState): boolean =>
  changed(prev, next, 'ux', 'calendar');
export const calendarAvailable = (ux: UxState): boolean => !!ux?.calendar;

export const selectionChanged = (prev: BookingState, post: BookingState): boolean => {
  if (prev.selection === post.selection) {
    return false;
  }
  return preDatesChanged(prev, post) || changed(prev, post, 'selection', 'travelDates');
};

export const allSelected = (selection: SelectionState): boolean => {
  if (!preDatesSelected(selection)) {
    return false;
  }

  const { travelDates, travelType, locations } = selection;

  if (isNotPresent(travelDates) || isNotPresent(travelDates.departureDate)) {
    return false;
  }

  if (travelType === TravelType.ONE_WAY) {
    return true;
  }

  if (travelType === TravelType.ROUND_TRIP && isPresent(travelDates.returnDate)) {
    return true;
  }

  return Boolean(
    locations[0]?.origin?.locationCode &&
      locations[0]?.destination?.locationCode &&
      locations[1]?.origin?.locationCode &&
      locations[1]?.destination?.locationCode &&
      isPresent(travelDates.returnDate)
  );
};

export const currentDateChanged = (prev: BookingState, next: BookingState): boolean =>
  changed(prev, next, 'ux', 'currentDate');
export const currentDateAvailable = ({ ux }: BookingState): boolean => isNotBlank(ux?.currentDate);

// TODO #am add validation when all data is available
export const validateAmFields = ({
  locations: { origin, destination, holidayType },
  amTravelDates: { departureDate, returnDate },
  duration,
  rooms,
}: AmSelectionState): boolean =>
  isPresent(origin) &&
  isPresent(destination) &&
  isPresent(departureDate) &&
  amDayIsSearchable(departureDate, destination) &&
  amPaxIsValid(rooms) &&
  ((holidayType.type === AmDurationType.DAYS &&
    isPresent(returnDate) &&
    amDayIsSearchable(returnDate, destination) &&
    holidayType.minDays <= daysBetween(departureDate, returnDate) &&
    daysBetween(departureDate, returnDate) <= holidayType.maxDays) ||
    (holidayType.type === AmDurationType.LIST && isPresent(duration) && durationIsValid(duration, holidayType)));

export const durationIsValid = (duration: string, holidayType: AmHolidayType): boolean => {
  if (!duration) {
    return true;
  }

  if (holidayType.type === AmDurationType.DAYS) {
    return true;
  }

  return !!holidayType.items.find((value) => value.code === duration);
};

const amRangeContains = (day: string, { from, to }: AmDateRange): boolean => {
  return !isBlank(from) && from <= day && !isBlank(to) && day <= to;
};

export const amDayIsSearchable = (day: string, location: AmLocation): boolean => {
  return (
    !location?.searchable || location.searchable.some((range: AmDateRange): boolean => amRangeContains(day, range))
  );
};

const amPaxIsValid = (rooms: AmRooms): boolean => {
  return rooms.every(
    (room: AmPaxAmount): boolean =>
      room.childrenAges.length === room.children && room.childrenAges.every((age) => isNotNull(age))
  );
};
